import { forwardRef, useCallback, useState } from 'react'
import { Dropdown, MenuProps, Modal } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from '../../hooks'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { deleteImage } from '../../Services/PIM/ImageService'
import { renderTranslation } from '../../Helpers/GenericHelpers'
import { deleteImageFromPhotoBox } from 'Slices/PIM/ImagesSlices'
import { PimImageTypeTranslations } from 'Constants/Configuration'

function DraggableImage(props, ref) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const missingImg = useAppSelector(state => state.present.language.missingImg)
  const [showModal, setShowModal] = useState(false)

  const deleteImageHandler = () => {
    const parsedId = parseInt(props.imageId)
    if (!parsedId) return
    dispatch(deleteImage(parsedId))
      .then(deletionCompleted => {
        deletionCompleted && dispatch(deleteImageFromPhotoBox(parsedId))
      })
      .finally(() => setShowModal(false))
  }

  const generateMenuItems = useCallback(
    (imageId): MenuProps['items'] => {
      return [
        {
          key: 'edit',
          label: renderTranslation(language, TranslationKeys.photoMenuEdit),
          onClick: e => {
            props.setEditId && props.setEditId(imageId)
          },
        },
        {
          key: 'delete',
          label: renderTranslation(language, TranslationKeys.photoMenuDelete),
          onClick: e => {
            setShowModal(true)
          },
        },
      ]
    },
    [language, props],
  )

  // // https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md
  const isDragging: boolean = props.draggableSnapshot?.isDragging

  return (
    <div className={props.containerClassName} ref={ref} {...props.draggableProps} {...props.dragHandleProps}>
      <img
        id={`${props.imageId}`}
        src={props.imageUrl}
        className={props.className}
        onError={e => {
          const target = e.target as HTMLImageElement
          target.onerror = null
          target.src = missingImg[TranslationKeys.notFoundImgPath]
        }}
        alt=''
      />
      {props.usedCount && props.usedCount !== 0 && !isDragging ? (
        <div className='counter'>{props.usedCount}</div>
      ) : null}
      {props.enableMenu && !isDragging && (
        <Dropdown menu={{ items: generateMenuItems(props.imageId) }} trigger={['click']}>
          <div className='menu'>
            <MoreOutlined className='generic-icon' />
          </div>
        </Dropdown>
      )}
      <Modal
        onCancel={() => setShowModal(false)}
        onOk={deleteImageHandler}
        title={renderTranslation(language, TranslationKeys.photoMenuDeleteConfirm)}
        open={showModal}
        closable={false}
        centered={true}
        className={'edit-photo-modal'}
      >
        {renderTranslation(language, TranslationKeys.photoMenuDeleteMessage)}
      </Modal>
      {props.type && !isDragging && (
        <div className='text-center pt-1'>
          {renderTranslation(
            language,
            PimImageTypeTranslations.find(type => {
              return type.key === props.type
            })?.translation,
          )}
        </div>
      )}
    </div>
  )
}

export default forwardRef(DraggableImage)
